import { CommonHelper, usePTVNAuthentication } from '@ptvn-react/authentication';
import environment from 'environment';
import { isEmpty } from 'lodash';
import moment from 'moment';
import { lazy, Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Route, Switch, useHistory } from 'react-router-dom';
import { Subscription } from 'rxjs';
import { AuthorizeEmarketplaceClient } from './api/AuthorizeEMarketplaceClient';
import { EMarketplaceClient } from './api/EMarketplaceClient';
import { GatewayGraphQLClient } from './api/GatewayGraphQLClient';
import EMarketplaceLayout from './components/layout/EMarketplaceLayout';
import { useModalStack } from './components/modal/ModalStackProvider';
import { SomethingWentWrongModal } from './components/modal/SomethingWentWrongModal';
import { useCompanyBranchDetailLazyQuery } from './graphql/autogenerate/hooks';
import { Company } from './graphql/autogenerate/schemas';
import { PrivateRoute } from './middlewares/privateRoute';
import { ExtendedUserInformation } from './models/User';
import { IndicatorFallback } from './provider/IndicatorProvider';
import { UserRoleType } from './utils/constants/JobBoardConstants';
import PageRoute, { RoutesItem } from './utils/constants/PageRoute';
import GaHelper from './utils/helpers/GAHelper';
import { oneTrustScript } from './utils/oneTrust';
import { map, mergeMap, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import Hotjar from '@hotjar/browser';
import GA4Helper from './utils/helpers/GA4Helper';
import useRedirect from './hooks/useRedirect';

const RedirectPage = lazy(() => import('./pages/RedirectPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const LogoutPage = lazy(() => import('./pages/LogoutPage'));
const SubscriptionPlanPageRouting = lazy(
  () => import('./pages/SubscriptionPlan/SubscriptionPlanPageRouting'),
);
const JobBoardPage = lazy(() => import('./pages/JobBoard'));
const MarketplacePage = lazy(() => import('./pages/Marketplace'));
const UnauthorizedPage = lazy(() => import('./pages/UnauthorizedPage'));
const PackageSettingPage = lazy(() => import('./pages/PackageSetting'));
const ConsentPage = lazy(() => import('./pages/ConsentPage'));
const UnderConstructionPage = lazy(() => import('./pages/UnderConstructionPage'));
const PIPage = lazy(() => import('./pages/PI'));
const NoPermissionsPage = lazy(() => import('./pages/NoPermissions'));

function App() {
  const {
    userInformation,
    currentCompany,
    setCompanies,
    setExtendedUserInformation,
    setPermissions,
    extendedUserInformation,
  } = usePTVNAuthentication<ExtendedUserInformation>();
  const { t, i18n } = useTranslation();
  const modalStack = useModalStack();
  const [userRole, setUserRole] = useState<UserRoleType>();
  const history = useHistory();
  const [profileErrorFallbackURL, setProfileErrorFallbackURL] = useState<string>();
  const { handleStoreRedirectLink } = useRedirect();

  const [
    loadCompanyBranchDetail,
    {
      data: graphQLCompanyBranchDetail,
      loading: isLoadingCompanyBranchDetail,
      error: errorCompanyBranchDetail,
    },
  ] = useCompanyBranchDetailLazyQuery({
    client: GatewayGraphQLClient,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    variables: {
      companyID: currentCompany?.companyID!,
      branchID: currentCompany?.branchId ? [currentCompany?.branchId] : [],
    },
  });

  useEffect(() => {
    handleStoreRedirectLink();
    oneTrustScript();
    Hotjar.init(environment.hotjar.siteId, environment.hotjar.hotjarVersion);
    GaHelper.initialize(environment.trackerId.ga);
    GaHelper.pageView();
    GA4Helper.initialize(environment.trackerId.ga4);
    let subscriptionGetMyProfile: Subscription;
    let userPermissionsSubscription: Subscription;
    if (userInformation && !document.URL.includes(PageRoute.LOGOUT)) {
      subscriptionGetMyProfile = AuthorizeEmarketplaceClient.getMyProfile().subscribe(
        (res) => {
          if (res.companies?.length === 0) {
            window.location.href = PageRoute.LOGOUT;
            return;
          }
          setExtendedUserInformation(res);
          if (res.companies) {
            setCompanies(
              res.companies.map((company, index) => {
                return {
                  companyID: company.eMarketplaceCompanyID,
                  branchId: !isEmpty(company.branchIDs) ? company.branchIDs[0] : undefined,
                  companyNameEN: '',
                  companyNameLocal: '',
                  branchNameEN: '',
                  branchNameLocal: '',
                  headQuarterBranch: true,
                  countryCode: '',
                  isDefault: index === 0,
                  isFullService: false,
                  taxID: '',
                  branchNumber: '',
                  orgID: '',
                };
              }),
            );
            setUserRole(res.companies[0].roles[0].roleName);
            userPermissionsSubscription = AuthorizeEmarketplaceClient.getUserPermissions(
              res.companies[0].eMarketplaceCompanyID,
              res.companies[0].roles[0].roleID,
            ).subscribe(
              (res) => {
                setPermissions(res.data);
              },
              (_err) => {
                setPermissions([]);
                CommonHelper.navigateToUrl(PageRoute.UNAUTHORIZED);
              },
            );
          }
        },
        (_err) => {
          if (_err && _err.data.errors[0].code === '4401') {
            setProfileErrorFallbackURL(PageRoute.UNAUTHORIZED);
          } else if (_err && _err.data.errors[0].code === '5031') {
            setProfileErrorFallbackURL(PageRoute.UNDER_CONSTRUCTION);
          } else {
            modalStack.push(SomethingWentWrongModal, {
              onSubmit: () => window.location.reload(),
            });
          }
        },
      );
    }

    return () => {
      subscriptionGetMyProfile && subscriptionGetMyProfile.unsubscribe();
      userPermissionsSubscription && userPermissionsSubscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (currentCompany && userRole) {
      switch (userRole) {
        case UserRoleType.SUPPLIER:
          loadCompanyBranchDetail();
          break;
        case UserRoleType.BUYER:
          EMarketplaceClient.getBuyerCompanyDetail(currentCompany.companyID)
            .pipe(
              map((res) => {
                const company: Company = {
                  companyId: 1,
                  companyNameEn: res.companyNameEN,
                  companyNameLocal: res.companyNameLocal,
                  branchList: [
                    {
                      branchNameEn: res.branchName,
                      branchNameLocal: res.branchName,
                      branchNumber: res.branchNumber,
                      branchAddressList: [],
                      categoryList: [],
                      contactList: [],
                      companyServiceRequestList: [],
                    },
                  ],
                };
                return {
                  ...extendedUserInformation,
                  currentCompanyBranchDetail: company,
                } as ExtendedUserInformation;
              }),
              mergeMap((extendedUserInformationRes) => {
                return EMarketplaceClient.getBuyerSubCompany(currentCompany.companyID).pipe(
                  map((res): ExtendedUserInformation => {
                    return {
                      ...extendedUserInformationRes,
                      currentERFXSubCompanyDetail: res.data,
                    };
                  }),
                  catchError(() => of(extendedUserInformationRes)),
                );
              }),
            )
            .subscribe((extendedUserInformationRes) => {
              setExtendedUserInformation(extendedUserInformationRes);
            });
          break;
      }
    }
  }, [currentCompany, userRole]);

  useEffect(() => {
    if (graphQLCompanyBranchDetail?.company) {
      setExtendedUserInformation({
        ...extendedUserInformation,
        currentCompanyBranchDetail: graphQLCompanyBranchDetail?.company as Company,
      });
    }
  }, [graphQLCompanyBranchDetail]);

  useEffect(() => {
    document.documentElement.lang = i18n.language;
    if (/^en.*/.test(i18n.language.toLowerCase())) {
      moment.locale('en');
    } else if (/^th.*/.test(i18n.language.toLowerCase())) {
      moment.updateLocale('th', {
        monthsShort: [
          'ม.ค.',
          'ก.พ.',
          'มี.ค.',
          'เม.ย.',
          'พ.ค.',
          'มิ.ย.',
          'ก.ค.',
          'ส.ค.',
          'ก.ย.',
          'ต.ค.',
          'พ.ย.',
          'ธ.ค.',
        ],
        weekdaysMin: ['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส'],
      });
    }
  }, [i18n.language]);

  useEffect(() => {
    if (profileErrorFallbackURL && history.location.pathname !== profileErrorFallbackURL) {
      history.push(profileErrorFallbackURL);
    }
  }, [history.location, profileErrorFallbackURL]);

  const publicRoutes: RoutesItem[] = [
    { path: PageRoute.REDIRECT, component: RedirectPage, exact: true },
    { path: PageRoute.LOGIN, component: LoginPage, exact: true },
  ];

  const publicEMarketplaceLayoutRoutes: RoutesItem[] = [
    { path: PageRoute.UNAUTHORIZED, component: UnauthorizedPage, exact: true },
    {
      path: PageRoute.UNDER_CONSTRUCTION,
      component: UnderConstructionPage,
      exact: true,
    },
    { path: PageRoute.NO_PERMISSION_SUBSCRIPTION, component: NoPermissionsPage, exact: true },
    {
      path: PageRoute.SUBSCRIPTION_PLAN,
      component: SubscriptionPlanPageRouting,
      exact: false,
    },
  ];

  const privateEMarketplaceLayoutRoutes: RoutesItem[] = [
    {
      path: PageRoute.PACKAGE_SETTING,
      component: PackageSettingPage,
      exact: true,
    },
    {
      path: PageRoute.CONSENT,
      component: ConsentPage,
      exact: true,
    },
    { path: PageRoute.JOB_BOARD, component: JobBoardPage, exact: false },
    { path: PageRoute.PI, component: PIPage, exact: false },
    { path: PageRoute.MARKETPLACE, component: MarketplacePage, exact: false },
  ];

  return (
    <Suspense fallback={<div>Loading ...</div>}>
      <Switch>
        <PrivateRoute path={PageRoute.LOGOUT} component={LogoutPage} exact />
        {publicRoutes.map((route) => (
          <Route {...route} key={route.path} />
        ))}
        <EMarketplaceLayout userRole={userRole}>
          <Suspense fallback={<IndicatorFallback />}>
            <Switch>
              {publicEMarketplaceLayoutRoutes.map(({ ...route }) => (
                <Route {...route} key={route.path} />
              ))}

              {privateEMarketplaceLayoutRoutes.map(({ ...route }) => (
                <PrivateRoute {...route} show={!!extendedUserInformation} key={route.path} />
              ))}
            </Switch>
          </Suspense>
        </EMarketplaceLayout>
      </Switch>
    </Suspense>
  );
}

export default App;
